import { useEffect, useMemo, useRef } from 'react';

import { useError, useCaptureException } from '@zenbusiness/application-commons-logging';

const ERROR_MESSAGES_AS_WARNING = ['Headers Timeout Error'];
const HTTP_STATUS_CODES_FOR_WARNING = [503, 401, 403];

/**
 * Custom hook to log errors using Sentry's captureException.
 * It captures the exception only once when the error is considered a warning.
 *
 * @param {Array} errors - ZBError[] Array of error objects to be logged.
 * @param {object} error - APIError<object> The error object containing graphQLErrors and httpError.
 */
export const useLogError = (errors, error) => {
  const captureException = useCaptureException();

  const { httpError } = error || {};
  const errorMessage = error?.message ?? '';

  const hasCaptured = useRef(false);

  // Determine if the error should be treated as a warning
  const isWarning =
    ERROR_MESSAGES_AS_WARNING.includes(errorMessage) ||
    HTTP_STATUS_CODES_FOR_WARNING.includes(httpError?.status);

  // Use the useError hook to handle errors
  useError(isWarning ? undefined : errors);

  // Memoize the warning message to avoid unnecessary recalculations
  const warningMessage = useMemo(() => {
    if (errorMessage) {
      return `WARNING: ${errorMessage}`;
    }
    if (httpError) {
      return `WARNING: GraphQL request failed due to an HTTP error (${httpError.status})`;
    }
    return 'WARNING: An error occurred';
  }, [errorMessage, httpError]);

  // Use useEffect to capture the exception only once when isWarning is true
  useEffect(() => {
    if (isWarning && !hasCaptured.current) {
      captureException(new Error(warningMessage), {
        level: 'warning'
      });
      hasCaptured.current = true;
    }
  }, [captureException, isWarning, warningMessage]);
};
